import { useState } from 'react';
import { extractAttributesList } from '@/utils/api-client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  evalService,
  projectCaseMetadata,
  PROJECT_CONTEXT_TASK_TYPE,
  type EvalCase,
  type EvalCaseCreateData,
  type TaggableProject,
} from '@/services/eval-service';
import { skillTemplateService, type SkillTemplate } from '@/services/skill-template-service';
import {
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  Spinner,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Checkbox,
  Label,
  toast,
} from 'prosci-ui';
import { Plus, Edit, Trash2, MoreHorizontal, FileText, BookOpenText } from '@/lib/icons';
import { GuidedCaseFields } from './guided-case-fields';
import { GUIDED_TASK_TYPE, guidedMetaOf, guidedMissingFields, type GuidedCaseMeta } from './guided-case';

type FixtureMessage = { role: string; content: string };
type FixtureConversation = { label: string; messages: FixtureMessage[] };

type CaseFixtures = {
  scenario?: string;
  expected_recall?: string;
  fixture_note?: string;
  fixture_conversations?: FixtureConversation[];
};

const caseFixtures = (evalCase: EvalCase): CaseFixtures | null => {
  const meta = evalCase.metadata as CaseFixtures | undefined;
  if (!meta?.scenario && !meta?.fixture_conversations?.length) return null;
  return meta;
};

type CaseForm = {
  name: string;
  query: string;
  expected_response: string;
  expected_domains: string;
  task_type: string;
  skill_template_id: string;
  is_active: boolean;
  guided: GuidedCaseMeta;
  project_id: string;
  expected_fields: string[];
};

const emptyForm: CaseForm = {
  name: '',
  query: '',
  expected_response: '',
  expected_domains: '',
  task_type: '',
  skill_template_id: '',
  is_active: true,
  guided: {},
  project_id: '',
  expected_fields: [],
};

export function TestCasesTab() {
  const [editingCase, setEditingCase] = useState<EvalCase | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CaseForm>(emptyForm);
  const [caseToDelete, setCaseToDelete] = useState<EvalCase | null>(null);
  const [fixtureCase, setFixtureCase] = useState<EvalCase | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['evalCases'],
    queryFn: () => evalService.listCases(),
  });

  const { data: skillsData } = useQuery({
    queryKey: ['skillTemplates'],
    queryFn: () => skillTemplateService.list(),
  });

  const skills: SkillTemplate[] =
    extractAttributesList(skillsData);

  const { data: projectsData } = useQuery({
    queryKey: ['evalTaggableProjects'],
    queryFn: () => evalService.listTaggableProjects(),
  });

  const taggableProjects: TaggableProject[] = projectsData?.projects || [];

  const { data: labelsData, isFetching: labelsLoading } = useQuery({
    queryKey: ['evalProjectLabels', form.project_id],
    queryFn: () => evalService.listProjectLabels(form.project_id),
    enabled: !!form.project_id,
  });

  const availableLabels: string[] = labelsData?.labels || [];
  const isProjectCase = !!form.project_id;

  const createMutation = useMutation({
    mutationFn: (data: EvalCaseCreateData) => evalService.createCase(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evalCases'] });
      toast.success('Test case created');
      closeForm();
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to create'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<EvalCaseCreateData> }) =>
      evalService.updateCase(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evalCases'] });
      toast.success('Test case updated');
      closeForm();
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to update'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => evalService.destroyCase(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evalCases'] });
      toast.success('Test case deleted');
      setCaseToDelete(null);
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to delete'),
  });

  const cases: EvalCase[] =
    extractAttributesList(data);

  const openCreate = () => {
    setEditingCase(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (evalCase: EvalCase) => {
    setEditingCase(evalCase);
    const meta = projectCaseMetadata(evalCase);
    setForm({
      name: evalCase.name,
      query: evalCase.query,
      expected_response: evalCase.expected_response || '',
      expected_domains: (evalCase.expected_domains || []).join(', '),
      task_type: evalCase.task_type || '',
      skill_template_id: evalCase.skill_template_id || '',
      is_active: evalCase.is_active,
      guided: guidedMetaOf(evalCase),
      project_id: meta.project_id || '',
      expected_fields: meta.expected_fields || [],
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingCase(null);
  };

  const isGuided = form.task_type === GUIDED_TASK_TYPE;
  const guidedMissing = isGuided ? guidedMissingFields(form.guided) : [];

  const handleSave = () => {
    // update replaces the whole jsonb column, so carry over keys this form doesn't edit (e.g. review-queue provenance).
    const preservedMetadata = { ...((editingCase?.metadata as Record<string, unknown>) ?? {}) };
    delete preservedMetadata.project_id;
    delete preservedMetadata.expected_fields;
    const payload: EvalCaseCreateData = {
      name: form.name,
      query: form.query,
      expected_response: form.expected_response || undefined,
      expected_domains: form.expected_domains ? form.expected_domains.split(',').map((s) => s.trim()).filter(Boolean) : [],
      task_type: (form.project_id ? PROJECT_CONTEXT_TASK_TYPE : form.task_type) || undefined,
      skill_template_id: form.skill_template_id || undefined,
      is_active: form.is_active,
      metadata: form.project_id
        ? { ...preservedMetadata, project_id: form.project_id, expected_fields: form.expected_fields }
        : preservedMetadata,
    };

    if (isGuided) {
      payload.metadata = {
        ...((editingCase?.metadata as Record<string, unknown>) ?? {}),
        canvas_key: form.guided.canvas_key,
        slot: form.guided.slot,
        expected_criteria_met: form.guided.expected_criteria_met,
      };
    }
    if (form.project_id) payload.expected_response = undefined;

    if (editingCase) {
      updateMutation.mutate({ id: editingCase.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New Test Case
        </Button>
      </div>

      {isLoading ? (
        <Empty><Spinner /></Empty>
      ) : cases.length === 0 ? (
        <Empty>
          <EmptyIcon><FileText className="h-12 w-12" /></EmptyIcon>
          <EmptyTitle>No test cases</EmptyTitle>
          <EmptyDescription>Create test cases to evaluate Kaiya's response quality.</EmptyDescription>
          <Button onClick={openCreate} className="mt-4">
            <Plus className="h-4 w-4 mr-2" />
            New Test Case
          </Button>
        </Empty>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[28%]">Name</TableHead>
                  <TableHead className="w-[30%]">Query</TableHead>
                  <TableHead className="w-[10%]">Skill</TableHead>
                  <TableHead className="w-[16%]">Type</TableHead>
                  <TableHead className="w-[10%]">Status</TableHead>
                  <TableHead className="w-[6%]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {cases.map((evalCase) => (
                  <TableRow key={evalCase.id}>
                    <TableCell className="font-medium text-sm">
                      <div className="truncate" title={evalCase.name}>{evalCase.name}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="truncate" title={evalCase.query}>{evalCase.query}</div>
                    </TableCell>
                    <TableCell>
                      {evalCase.skill_template_name ? (
                        <Badge variant="outline" className="truncate max-w-full">{evalCase.skill_template_name}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">Global</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {evalCase.task_type ? (
                        <div className="truncate" title={evalCase.task_type}>
                          <Badge variant="outline" className="truncate max-w-full text-xs">{evalCase.task_type}</Badge>
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={evalCase.is_active ? 'default' : 'secondary'}>
                        {evalCase.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {caseFixtures(evalCase) && (
                            <DropdownMenuItem onClick={() => setFixtureCase(evalCase)}>
                              <BookOpenText className="h-4 w-4 mr-2" /> View setup
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => openEdit(evalCase)}>
                            <Edit className="h-4 w-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setCaseToDelete(evalCase)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={closeForm}>
        <DialogContent className="sm:!max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingCase ? 'Edit Test Case' : 'New Test Case'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Name</label>
              <input
                type="text"
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                placeholder="ADKAR model question"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Linked Skill</label>
              <select
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm bg-background"
                value={form.skill_template_id}
                onChange={(e) => setForm({ ...form, skill_template_id: e.target.value })}
              >
                <option value="">None (Global)</option>
                {skills.map((skill) => (
                  <option key={skill.id} value={skill.id}>
                    {skill.display_title}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Link this test case to a specific skill for per-skill regression testing.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Case kind</label>
              <select
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                value={isGuided ? GUIDED_TASK_TYPE : 'chat'}
                onChange={(e) =>
                  setForm({
                    ...form,
                    task_type: e.target.value === GUIDED_TASK_TYPE ? GUIDED_TASK_TYPE : '',
                    guided: {},
                  })
                }
              >
                <option value="chat">Kaiya Core</option>
                <option value={GUIDED_TASK_TYPE}>Guided use case</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">{isGuided ? 'Practitioner draft' : 'Query'}</label>
              <textarea
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                rows={3}
                placeholder={isGuided ? 'We will get people using the new system quickly.' : 'What is the ADKAR model?'}
                value={form.query}
                onChange={(e) => setForm({ ...form, query: e.target.value })}
              />
              {isGuided && (
                <p className="text-xs text-muted-foreground mt-1">
                  The draft Kaiya is asked to grade. Paste it exactly as a practitioner would have written it.
                </p>
              )}
            </div>
            <div className={`rounded-md border p-3 space-y-3${isGuided ? ' hidden' : ''}`}>
              <label className="text-sm font-medium">Attach a Proxima project</label>
              <div>
                <label className="text-xs font-medium">Proxima project</label>
                <select
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm bg-background"
                  value={form.project_id}
                  onChange={(e) => setForm({ ...form, project_id: e.target.value, expected_fields: [] })}
                >
                  <option value="">No project (plain case)</option>
                  {taggableProjects.map((project) => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </div>

              {isProjectCase && (
                <div>
                  <label className="text-xs font-medium">
                    Fields the answer must report ({form.expected_fields.length} selected)
                  </label>
                  <p className="text-xs text-muted-foreground mt-1 mb-2">
                    Values are read live from the project at run time, so there is nothing to keep
                    up to date. Only fields the methodology review cleared are listed.
                  </p>
                  {labelsLoading ? (
                    <Spinner />
                  ) : (
                    <div className="max-h-48 overflow-y-auto rounded-md border p-2 space-y-1">
                      {availableLabels.map((label) => (
                        <Label key={label} className="flex items-center gap-2 text-xs cursor-pointer">
                          <Checkbox
                            checked={form.expected_fields.includes(label)}
                            onCheckedChange={(checked) =>
                              setForm({
                                ...form,
                                expected_fields: checked
                                  ? [...form.expected_fields, label]
                                  : form.expected_fields.filter((f) => f !== label),
                              })
                            }
                          />
                          {label}
                        </Label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Expected Response</label>
              <textarea
                className={`w-full mt-1 px-3 py-2 border rounded-md text-sm ${
                  isProjectCase ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed' : ''
                }`}
                rows={3}
                disabled={isProjectCase}
                placeholder="Describe the expected response criteria for the LLM judge..."
                value={isProjectCase ? '' : form.expected_response}
                onChange={(e) => setForm({ ...form, expected_response: e.target.value })}
              />
              {!isProjectCase && (
                <p className="text-xs text-muted-foreground mt-1">
                  The LLM judge will evaluate the actual response against these criteria for correctness, completeness, and relevance.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Expected Domains (comma-separated)</label>
                <input
                  type="text"
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                  placeholder="adkar, change-management"
                  value={form.expected_domains}
                  onChange={(e) => setForm({ ...form, expected_domains: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Task Type</label>
                <input
                  type="text"
                  className={`w-full mt-1 px-3 py-2 border rounded-md text-sm ${
                    isProjectCase ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed' : ''
                  }`}
                  placeholder="qa, document_generation"
                  disabled={isProjectCase}
                  value={isProjectCase ? PROJECT_CONTEXT_TASK_TYPE : form.task_type}
                  onChange={(e) => setForm({ ...form, task_type: e.target.value })}
                />
              </div>
            </div>

            {isGuided && (
              <GuidedCaseFields
                value={form.guided}
                onChange={(guided) => setForm({ ...form, guided })}
              />
            )}
            <Label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: !!checked })}
              />
              Active
            </Label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeForm}>Cancel</Button>
            {guidedMissing.length > 0 && (
              <span className="text-xs text-muted-foreground self-center">
                Still needs {guidedMissing.join(', ')}
              </span>
            )}
            <Button onClick={handleSave} disabled={isSaving || guidedMissing.length > 0}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fixture setup dialog: what history to seed and what Kaiya should pick up */}
      <Dialog open={!!fixtureCase} onOpenChange={() => setFixtureCase(null)}>
        <DialogContent className="sm:!max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Test Setup — {fixtureCase?.name}</DialogTitle>
          </DialogHeader>
          {fixtureCase && (() => {
            const fixtures = caseFixtures(fixtureCase);
            if (!fixtures) return null;
            return (
              <div className="space-y-4 text-sm">
                {fixtures.scenario && (
                  <div>
                    <h4 className="font-medium mb-1">Scenario</h4>
                    <p className="text-muted-foreground">{fixtures.scenario}</p>
                  </div>
                )}
                {fixtures.fixture_note && (
                  <div className="rounded-md border bg-muted/50 p-3 text-muted-foreground">
                    {fixtures.fixture_note}
                  </div>
                )}
                {(fixtures.fixture_conversations || []).length > 0 ? (
                  (fixtures.fixture_conversations || []).map((conversation, i) => (
                    <div key={i}>
                      <h4 className="font-medium mb-2">{conversation.label}</h4>
                      <div className="space-y-2 rounded-md border p-3">
                        {conversation.messages.map((message, j) => (
                          <div key={j} className="flex gap-2">
                            <Badge
                              variant={message.role === 'user' ? 'default' : 'secondary'}
                              className="h-fit shrink-0 capitalize"
                            >
                              {message.role}
                            </Badge>
                            <p className="whitespace-pre-wrap">{message.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div>
                    <h4 className="font-medium mb-1">Fixture conversations</h4>
                    <p className="text-muted-foreground">
                      None — this case intentionally runs with no relevant prior history.
                    </p>
                  </div>
                )}
                <div>
                  <h4 className="font-medium mb-1">Eval query</h4>
                  <p className="text-muted-foreground">{fixtureCase.query}</p>
                </div>
                {fixtures.expected_recall && (
                  <div>
                    <h4 className="font-medium mb-1">What Kaiya should pick up</h4>
                    <p className="text-muted-foreground">{fixtures.expected_recall}</p>
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFixtureCase(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!caseToDelete} onOpenChange={() => setCaseToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Test Case</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Delete "{caseToDelete?.name}"? This will remove all associated results.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCaseToDelete(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => caseToDelete && deleteMutation.mutate(caseToDelete.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
